import { useEffect, useMemo, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Download, ScrollText } from "lucide-react";
import {
  Button,
  FeedbackState,
  FormDrawer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
} from "@h2j-data-team/ui";

import { cn } from "@/lib/utils";
import { useFormatDate } from "@/hooks/use-format-date";
import { VirtualRowList } from "@/components/shared/VirtualRowList";

// A worker execution-log record, shared by every run-log source (QVD runs, app
// analyses). The level enum ("info" | "warn" | "error") is identical across
// sources, so a single row type serves all of them.
export type RunLogLevel = "info" | "warn" | "error";
export interface RunLogRow {
  id: string;
  ts: string;
  level: RunLogLevel;
  message: string;
  attrs: Record<string, unknown> | null;
}

const LEVEL_STYLES: Record<RunLogLevel, string> = {
  info: "text-faint",
  warn: "text-status-warning-foreground",
  error: "text-status-danger-foreground",
};

// One log line's structured attrs, flattened to "key=value" pairs for the
// single-line view (the full record is in the row's title tooltip).
function formatAttrs(attrs: RunLogRow["attrs"]): string {
  if (!attrs) return "";
  return Object.entries(attrs)
    .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join("  ");
}

// Presentational worker execution-log stream: a level filter, a virtualized row
// list (rows stream in live while `active`), empty states, and an export button.
// The data + handlers are injected so this works for any run-log source; i18n
// keys are read from the `ns` namespace under `logs.*`.
export function RunLogsList({
  ns,
  rows,
  isLoading,
  isError,
  active,
  level,
  onLevelChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onExport,
}: {
  ns: string;
  rows: RunLogRow[];
  isLoading: boolean;
  isError: boolean;
  active: boolean;
  level: RunLogLevel;
  onLevelChange: (level: RunLogLevel) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onExport: () => void;
}) {
  const { t } = useTranslation(ns);
  const { t: tc } = useTranslation("common");
  const { formatDateTime } = useFormatDate();
  // Rows expanded (wrapped, fully visible) by a click. Kept as a set so several
  // lines can be open at once for comparison.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const filtering = level !== "info";

  // While the run is active, pull every page as soon as it exists so the tail
  // keeps streaming past the page size without manual scrolling (bounded by the
  // worker's per-run log cap). Finished runs keep scroll-based loading.
  useEffect(() => {
    if (active && hasNextPage && !isFetchingNextPage) onLoadMore();
  }, [active, hasNextPage, isFetchingNextPage, onLoadMore]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const columns = useMemo<DataTableColumn<RunLogRow>[]>(
    () => [
      {
        id: "ts",
        header: "",
        width: 170,
        cell: (l) => <span className="text-faint">{formatDateTime(l.ts)}</span>,
      },
      {
        id: "level",
        header: "",
        width: 52,
        cell: (l) => (
          <span
            className={cn(
              "text-[10.5px] font-bold uppercase tracking-[0.06em]",
              LEVEL_STYLES[l.level],
            )}
          >
            {l.level}
          </span>
        ),
      },
      {
        id: "message",
        header: "",
        wrap: true,
        cell: (l) => {
          const attrs = formatAttrs(l.attrs);
          const isOpen = expanded.has(l.id);
          return (
            <button
              type="button"
              aria-expanded={isOpen}
              title={isOpen ? undefined : t("logs.expandHint")}
              onClick={() => {
                // Don't collapse/expand when the user is selecting text to copy —
                // that's the whole point of expanding a line.
                if (window.getSelection()?.toString()) return;
                toggleExpanded(l.id);
              }}
              className={cn(
                "block w-full rounded-sm text-left -outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring",
                isOpen ? "whitespace-pre-wrap wrap-break-word" : "truncate",
              )}
            >
              <span className="text-ink">{l.message}</span>
              {attrs && (
                <span className="text-faint">
                  {"  "}
                  {attrs}
                </span>
              )}
            </button>
          );
        },
      },
    ],
    [t, formatDateTime, expanded],
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <Button variant="outline" size="sm" onClick={onExport} disabled={rows.length === 0}>
          <Download />
          {t("logs.export")}
        </Button>
        <Select value={level} onValueChange={(v) => onLevelChange(v as RunLogLevel)}>
          <SelectTrigger size="compact" className="w-48" aria-label={tc("runLogs.levelFilter")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="info">{t("logs.level.all")}</SelectItem>
            <SelectItem value="warn">{t("logs.level.warn")}</SelectItem>
            <SelectItem value="error">{t("logs.level.error")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <FeedbackState state="loading" title={tc("list.loading")} />
      ) : isError ? (
        <FeedbackState state="error" title={t("logs.error")} />
      ) : rows.length === 0 ? (
        <FeedbackState
          state={filtering ? "no-results" : "empty"}
          icon={<ScrollText />}
          title={filtering ? t("logs.empty.filteredTitle") : t("logs.empty.title")}
          description={
            filtering
              ? t("logs.empty.filteredDescription")
              : active
                ? t("logs.empty.activeDescription")
                : t("logs.empty.description")
          }
        />
      ) : (
        <VirtualRowList
          label={t("logs.title")}
          showHeader={false}
          columns={columns}
          rows={rows}
          getKey={(l) => l.id}
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) onLoadMore();
          }}
          loadingMoreLabel={isFetchingNextPage ? tc("list.loading") : undefined}
        />
      )}
    </div>
  );
}

// RunLogsDrawer is the debug surface for a run/analysis: a right-side drawer
// opened from the header's "Logs" button — out of the way of the results, since
// the log stream is a diagnostic tool, not a result. The body (a RunLogsList
// container) is passed as children so each source binds its own data.
export function RunLogsDrawer({
  open,
  onOpenChange,
  ns,
  children,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ns: string;
  children: ReactNode;
  /** The "Logs" button, for the focus to return to when the drawer closes. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation(ns);
  const { t: tc } = useTranslation("common");
  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t("logs.title")}
      description={t("logs.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
    >
      {children}
    </FormDrawer>
  );
}
